/**
 * Performance Monitoring Utilities
 *
 * Lightweight performance tracking for measuring page load times,
 * component render times, and other performance metrics.
 */

interface PerformanceMetrics {
  name: string
  duration: number
  timestamp: number
}

class PerformanceMonitor {
  private metrics: PerformanceMetrics[] = []
  private marks: Map<string, number> = new Map()

  /**
   * Start timing an operation
   */
  start(name: string): void {
    this.marks.set(name, performance.now())
  }

  /**
   * End timing an operation and record the metric
   */
  end(name: string): number | null {
    const startTime = this.marks.get(name)
    if (!startTime) {
      console.warn(`No start mark found for: ${name}`)
      return null
    }

    const duration = performance.now() - startTime
    this.marks.delete(name)

    this.metrics.push({
      name,
      duration,
      timestamp: Date.now(),
    })

    // Log in development
    if (import.meta.env.DEV) {
      console.log(`⏱️ ${name}: ${duration.toFixed(2)}ms`)
    }

    return duration
  }

  /**
   * Get all recorded metrics
   */
  getMetrics(): PerformanceMetrics[] {
    return [...this.metrics]
  }

  /**
   * Get average duration for a specific metric
   */
  getAverage(name: string): number {
    const filtered = this.metrics.filter((m) => m.name === name)
    if (filtered.length === 0) return 0

    const sum = filtered.reduce((acc, m) => acc + m.duration, 0)
    return sum / filtered.length
  }

  /**
   * Clear all metrics
   */
  clear(): void {
    this.metrics = []
    this.marks.clear()
  }

  /**
   * Get a summary of all metrics
   */
  getSummary(): Record<string, { count: number; avg: number; min: number; max: number }> {
    const summary: Record<string, { count: number; avg: number; min: number; max: number }> =
      {}

    this.metrics.forEach((metric) => {
      if (!summary[metric.name]) {
        summary[metric.name] = {
          count: 0,
          avg: 0,
          min: Infinity,
          max: -Infinity,
        }
      }

      const s = summary[metric.name]
      s.count++
      s.min = Math.min(s.min, metric.duration)
      s.max = Math.max(s.max, metric.duration)
    })

    // Calculate averages
    Object.keys(summary).forEach((name) => {
      summary[name].avg = this.getAverage(name)
    })

    return summary
  }

  /**
   * Print performance summary to console
   */
  printSummary(): void {
    const summary = this.getSummary()

    console.group('📊 Performance Summary')
    Object.entries(summary).forEach(([name, stats]) => {
      console.log(
        `${name}: avg=${stats.avg.toFixed(2)}ms, min=${stats.min.toFixed(2)}ms, max=${stats.max.toFixed(2)}ms, count=${stats.count}`
      )
    })
    console.groupEnd()
  }
}

// Singleton instance
export const perfMonitor = new PerformanceMonitor()

/**
 * React hook for measuring component render time
 * Import React in the component file and use this hook
 */
export function usePerformanceMonitor(componentName: string, useEffect: any) {
  useEffect(() => {
    perfMonitor.start(`${componentName}-render`)

    return () => {
      perfMonitor.end(`${componentName}-render`)
    }
  })
}

/**
 * Get Web Vitals metrics (if available)
 */
export function getWebVitals(): {
  fcp?: number // First Contentful Paint
  lcp?: number // Largest Contentful Paint
  fid?: number // First Input Delay
  cls?: number // Cumulative Layout Shift
  ttfb?: number // Time to First Byte
} {
  const vitals: any = {}

  // Try to get navigation timing
  if ('performance' in window && 'timing' in performance) {
    const timing = performance.timing
    vitals.ttfb = timing.responseStart - timing.requestStart
  }

  // Try to get paint timing
  if ('performance' in window && 'getEntriesByType' in performance) {
    const paintEntries = performance.getEntriesByType('paint')
    const fcpEntry = paintEntries.find((entry) => entry.name === 'first-contentful-paint')
    if (fcpEntry) {
      vitals.fcp = fcpEntry.startTime
    }
  }

  return vitals
}

/**
 * Log initial page load metrics
 */
export function logPageLoadMetrics(): void {
  if (typeof window === 'undefined') return

  window.addEventListener('load', () => {
    setTimeout(() => {
      const vitals = getWebVitals()

      console.group('🚀 Page Load Metrics')
      console.log(`Time to First Byte: ${vitals.ttfb?.toFixed(2)}ms`)
      console.log(`First Contentful Paint: ${vitals.fcp?.toFixed(2)}ms`)
      console.groupEnd()

      // Log navigation timing
      if ('performance' in window && 'timing' in performance) {
        const timing = performance.timing
        const loadTime = timing.loadEventEnd - timing.navigationStart
        const domReady = timing.domContentLoadedEventEnd - timing.navigationStart

        console.group('📈 Detailed Timing')
        console.log(`Total Load Time: ${loadTime}ms`)
        console.log(`DOM Ready: ${domReady}ms`)
        console.log(`DOM Parse: ${timing.domComplete - timing.domLoading}ms`)
        console.groupEnd()
      }
    }, 0)
  })
}

// Auto-log page load metrics in development
if (import.meta.env.DEV) {
  logPageLoadMetrics()
}

// Expose to window for debugging
if (import.meta.env.DEV) {
  ;(window as any).perfMonitor = perfMonitor
}
